import { useEffect, useState } from "preact/hooks";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { authClient } from "../lib/auth-client";
import { useDocumentTitle } from "../lib/use-document-title";
import { obtainTurnstileToken, prepareTurnstile } from "../lib/turnstile";
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  SectionHeading,
} from "../components/ui";
import heroImage from "../assets/login-hero.webp";

export function Login() {
  useDocumentTitle("サインイン");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Sitekey is loaded from the Worker (`GET /api/config`) so it's defined in
  // one place (wrangler.jsonc vars) instead of duplicated into the bundle.
  // While null, the submit button stays disabled — there's nothing to mint
  // a token against yet.
  const [turnstileSitekey, setTurnstileSitekey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Start the external script while the user types. A failed warm-up is
    // intentionally silent: obtainTurnstileToken retries it on submit and
    // owns the user-facing error state.
    void prepareTurnstile().catch(() => {});
    void (async () => {
      try {
        const res = await fetch("/api/config", { credentials: "omit" });
        if (!res.ok) return;
        const data = (await res.json()) as { turnstileSitekey?: string };
        if (!cancelled && data.turnstileSitekey) {
          setTurnstileSitekey(data.turnstileSitekey);
        }
      } catch {
        // Config fetch failure leaves the button disabled — surfaces as a
        // long loading state, which is the right UX cue.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSubmit(e: Event) {
    e.preventDefault();
    // Guard double-submit: the button is disabled while `sending`, but the
    // Turnstile token mint is async so a fast second Enter could slip in before
    // the disabled state applies and start a second challenge.
    if (!turnstileSitekey || sending) return;
    setError(null);
    setSending(true);
    try {
      // Mint a fresh Turnstile token per submit. On-demand mount-unmount
      // avoids the iOS Safari AutoFill iframe overlay bug — the widget only
      // exists in the DOM for the few hundred ms of the challenge.
      const token = await obtainTurnstileToken({
        sitekey: turnstileSitekey,
        action: "magic-link",
      });
      const { error } = await authClient.signIn.magicLink(
        { email, callbackURL: "/" },
        { headers: { "x-turnstile-token": token } },
      );
      if (error) {
        // A send failure inside the magic-link plugin (Resend quota gone,
        // Resend down, RESEND_API_KEY missing) propagates uncaught through
        // better-call, which answers with an empty-bodied 500 — so `error` is
        // just { status, statusText } and there is no message to show. The
        // cause is deliberately not distinguishable to the client here, so keep
        // the wording generic; the reason is in the Worker logs.
        setError(
          error.message ??
            "メールを送信できませんでした。時間をおいてからもう一度お試しください。",
        );
      } else {
        setSent(true);
      }
    } catch (e) {
      const msg = (e as Error).message;
      // Translate the helper's machine errors into a user-readable string.
      if (msg === "turnstile_timeout" || msg.startsWith("turnstile_error:")) {
        setError("認証チェックに失敗しました。もう一度お試しください。");
      } else if (msg === "turnstile_script_load_failed") {
        setError(
          "認証スクリプトの読み込みに失敗しました。ネットワークをご確認ください。",
        );
      } else {
        setError(msg);
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <section class="mx-auto max-w-4xl">
      <Card elevated class="overflow-hidden">
        <div class="grid gap-0 sm:grid-cols-2">
          <div class="p-8 sm:p-10">
            <SectionHeading
              level={2}
              title="Elwood CMS にサインイン"
              description="メールアドレスにサインインリンクを送ります。"
            />

            {sent ? (
              <div class="mt-6">
                <Alert tone="success" title="メールを送信しました">
                  {email} に届いたリンクをクリックしてサインインしてください。リンクは 15 分で失効します。
                </Alert>
              </div>
            ) : (
              <form onSubmit={onSubmit} class="mt-6 space-y-4">
                <Field label="メールアドレス" htmlFor="login-email">
                  <Input
                    id="login-email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onInput={(e) =>
                      setEmail((e.target as HTMLInputElement).value)
                    }
                    placeholder="you@example.com"
                  />
                </Field>
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  fullWidth
                  loading={sending}
                  disabled={sending || !turnstileSitekey}
                  iconRight={!sending && <ArrowRightIcon class="h-4 w-4" />}
                >
                  {sending ? "送信中…" : "サインインリンクを送信"}
                </Button>
                {error && <Alert tone="danger">{error}</Alert>}
                <p class="pt-1 text-xs text-fg-subtle">
                  アカウントは招待制です。招待されていないメールアドレスはサインインできません。
                </p>
                <p class="text-[11px] leading-relaxed text-fg-faint">
                  このフォームは bot 防止のため Cloudflare Turnstile を使用しています。
                  使用については{" "}
                  <a
                    href="https://www.cloudflare.com/turnstile-privacy-policy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="underline underline-offset-2 hover:text-fg-muted"
                  >
                    Cloudflare Turnstile Privacy Addendum
                  </a>
                  {" "}が適用されます。個人情報の取り扱いについては{" "}
                  <a
                    href="https://lp.elwood.akikan.app/privacy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="underline underline-offset-2 hover:text-fg-muted"
                  >
                    プライバシーポリシー
                  </a>
                  {" "}をご確認ください。
                </p>
              </form>
            )}
          </div>
          <div
            aria-hidden="true"
            class="hidden border-l border-border-subtle bg-bg-subtle sm:block"
          >
            <img
              src={heroImage}
              alt=""
              class="h-full w-full object-cover"
              loading="eager"
              decoding="async"
            />
          </div>
        </div>
      </Card>
    </section>
  );
}
